import { getFromDataPath, reduceDataPath } from "../../../utility";
import { isGroupingFactor } from "../../typeGuards";
import {
  ARSTActionDropAction,
  ArsTableBody,
  ArsTableCellData,
  ArsTableGroup,
  ArsTableSidebarItemKind,
  ArsTableState,
  RIGHT_EDGE_ZONES,
  defaultRowHeight,
} from "../interface";
import { isArsTableSubGroup, isArsTableSubGroupGroup } from "../typeguards/isArsTableSubGroup";
import { arst_groupingFactorToTableGroup, arst_tableGroupDepth } from "./arst_arsToTableGroup";
import { arst_insertTableGroups } from "./arst_insertTableGroups";
import { blankRowHeaderCell } from "./blankRowHeaderCell";

/**
 * Resolves the relative insertion path for column group insertion.
 *
 * @param targetCell - Scaffold cell that received the drop.
 * @param columns - Current root column groups.
 * @param isAfterZone - Whether the drop zone indicates insertion after the target.
 * @param isT - Label type guard.
 * @returns Relative insertion path for `arst_insertTableGroups`.
 */
const resolveColumnInsertDataPath = <T>(
  targetCell: ArsTableCellData<T> | undefined,
  columns: ArsTableGroup<T>[],
  isAfterZone: boolean,
  isT: (v: unknown) => v is T,
): string => {
  if (!targetCell) return "/0";

  const pathParts = targetCell.dataPath.split("/").filter(Boolean);
  if (pathParts[0] !== "columns") return "/0";
  const relativePath = `/${pathParts.slice(1).join("/")}`;
  if (pathParts.length === 2) {
    const rootIndex = parseInt(pathParts[1], 10);
    return Number.isNaN(rootIndex) ? "/0" : `/${isAfterZone ? rootIndex + 1 : rootIndex}`;
  }

  const parentPath = reduceDataPath(relativePath, 2);
  const parentGroup = getFromDataPath(parentPath, columns, (obj) => isArsTableSubGroup(obj, isT));
  if (!parentGroup) return "/0";

  const childIndex = parseInt(pathParts[pathParts.length - 1], 10);
  const isDataDriven =
    (isArsTableSubGroupGroup(parentGroup, isT) && (parentGroup.isDataDriven ?? false)) ||
    ("groupingFactor" in parentGroup && parentGroup.groupingFactor.dataDriven);
  const currentSubGroups = parentGroup.subGroups ?? [];
  const insertAt = isDataDriven
    ? isAfterZone
      ? currentSubGroups.length
      : 0
    : isAfterZone
      ? childIndex + 1
      : childIndex;
  return Number.isNaN(insertAt) ? "/0" : `${parentPath}/subGroups/${insertAt}`;
};

/**
 * Inserts dropped grouping factors into the column hierarchy.
 *
 * @param state - Current ARS table state
 * @param action - Drop action describing the target column position
 * @returns Updated ARS table state
 */
export const arst_insertColumn = <T>(
  state: ArsTableState<T>,
  action: ARSTActionDropAction,
): ArsTableState<T> => {
  const newTableGroups = action.payload
    .map((item) => item.dropItem)
    .filter((item) => isGroupingFactor(item))
    .map((groupingFactor) =>
      arst_groupingFactorToTableGroup(groupingFactor, state.settings.editorProps),
    );
  const targetCell = state.scaffoldModel?.cellData?.[action.rowId]?.[action.columnId];

  let newBodyData: ArsTableBody<T> = state.bodyData;

  const insertDataPath = resolveColumnInsertDataPath(
    targetCell,
    state.bodyData.columns,
    RIGHT_EDGE_ZONES.has(action.zone),
    state.settings.editorProps.isT,
  );

  const insertedColumns = arst_insertTableGroups(
    state.bodyData.columns,
    newTableGroups,
    insertDataPath,
    state.settings.editorProps.isT,
  );
  newBodyData = { ...state.bodyData, columns: insertedColumns };

  let newRowHeaders = newBodyData.rowHeaders;
  const newColumnDepth = Math.max(
    ...newBodyData.columns.map((group) => arst_tableGroupDepth(group, state.settings.editorProps)),
    1,
  );
  const currentRowHeights =
    newBodyData.rowHeaders.length > 0 ? newBodyData.rowHeaders[0].heights : [];

  if (currentRowHeights.length !== newColumnDepth) {
    const newRowHeights = [
      ...currentRowHeights.slice(0, newColumnDepth),
      ...Array.from({ length: newColumnDepth - currentRowHeights.length }, () => defaultRowHeight),
    ];
    const rowHeaderDepth = Math.max(
      ...state.bodyData.rows.map((group) =>
        arst_tableGroupDepth(group, state.settings.editorProps),
      ),
      1,
    );
    newRowHeaders = [
      ...newRowHeaders.map((rowHeader) => ({ ...rowHeader, heights: newRowHeights })),
      ...Array.from({ length: rowHeaderDepth - newRowHeaders.length }, () => ({
        ...blankRowHeaderCell(state.settings.editorProps.blankT, newRowHeights),
      })),
    ];
  }

  const finalBodyData: ArsTableBody<T> = { ...newBodyData, rowHeaders: newRowHeaders };
  const newUsedGroupingFactorIds = new Set(state.usedGroupingFactorIds);
  action.payload.forEach((item) => {
    if (item.kind === ArsTableSidebarItemKind.GroupingFactor) {
      newUsedGroupingFactorIds.add(item.dropItem.id);
    }
  });

  return {
    ...state,
    bodyData: finalBodyData,
    usedGroupingFactorIds: newUsedGroupingFactorIds,
    changed: true,
  };
};
